import React, { useState, useEffect } from 'react'
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { useDevices } from '../hooks/useDevices'
import { 
  getCustomerUsers, 
  createOrUpdateUser, 
  deleteUser, 
  updateUserStatus,
  ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  getUserPermissionsFromRoles
} from '../services/permissionService'

const UserManagement = () => {
  const { customer, hasPermission, userPermissions, isSuperAdmin, PERMISSIONS: PERMS } = useAuth()
  const { devices } = useDevices()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddUser, setShowAddUser] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [legacyDevices, setLegacyDevices] = useState([])
  const [deviceSearch, setDeviceSearch] = useState('')
  const [newUser, setNewUser] = useState({
    emailId: '',
    roles: [ROLES.DEVICE_VIEWER],
    metadata: { name: '' },
    status: 'active',
    device_restrictions: [],
    custom_permissions: []
  })

  // Check if user has access to user management
  const canViewUsers = hasPermission(PERMS.USERS.VIEW)
  const canEditUsers = hasPermission(PERMS.USERS.EDIT)
  const canInviteUsers = hasPermission(PERMS.USERS.INVITE)
  const canDeleteUsers = hasPermission(PERMS.USERS.DELETE)

  // Load users on component mount
  useEffect(() => {
    if (customer && canViewUsers) {
      loadUsers()
    }
  }, [customer, canViewUsers])

  // Reset device search when either modal opens or closes
  useEffect(() => { setDeviceSearch('') }, [showAddUser])
  useEffect(() => { setDeviceSearch('') }, [!!editingUser])

  // Fetch legacy devices for the restriction picker
  useEffect(() => {
    if (!customer) return
    const fetchLegacyDevices = async () => {
      try {
        const devicesQuery = query(collection(db, 'device'), where('customer', '==', customer))
        const [devicesSnap, touchSnap] = await Promise.all([
          getDocs(devicesQuery),
          getDocs(collection(db, `${customer}_machines`)).catch(() => ({ docs: [] }))
        ])
        const touchIds = new Set(touchSnap.docs.map(d => d.id))
        const legacy = devicesSnap.docs
          .map(d => ({ _docId: d.id, ...d.data() }))
          .filter(d => !touchIds.has(d.name))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        setLegacyDevices(legacy)
      } catch (err) {
        console.error('Error fetching legacy devices for picker:', err)
      }
    }
    fetchLegacyDevices()
  }, [customer])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const customerUsers = await getCustomerUsers(customer)
      setUsers(customerUsers)
    } catch (err) {
      setError('Failed to load users')
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = async () => {
    try {
      if (!newUser.emailId || !newUser.metadata.name) {
        setError('Email and name are required')
        return
      }

      // Compute permissions: strip DL from role defaults, then apply checkbox as sole authority
      const rolePerms = getUserPermissionsFromRoles(newUser.roles)
        .filter(p => p !== PERMISSIONS.DOWNLOADS.ACCESS)
      const downloadsGranted = newUser.custom_permissions.includes(PERMISSIONS.DOWNLOADS.ACCESS)
      const mergedPerms = downloadsGranted
        ? [...new Set([...rolePerms, PERMISSIONS.DOWNLOADS.ACCESS])]
        : rolePerms

      const userData = {
        ...newUser,
        permissions: mergedPerms,
        created_by: userPermissions?.emailId || 'admin'
      }

      const success = await createOrUpdateUser(customer, newUser.emailId, userData)
      if (success) {
        await loadUsers()
        setShowAddUser(false)
        setNewUser({
          emailId: '',
          roles: [ROLES.DEVICE_VIEWER],
          metadata: { name: '' },
          status: 'active',
          device_restrictions: [],
          custom_permissions: []
        })
        setError('')
      } else {
        setError('Failed to add user')
      }
    } catch (err) {
      setError('Error adding user: ' + err.message)
    }
  }

  const handleUpdateUser = async (user) => {
    try {
      // Compute permissions: strip DL from role defaults, then apply checkbox as sole authority
      const rolePerms = getUserPermissionsFromRoles(user.roles)
        .filter(p => p !== PERMISSIONS.DOWNLOADS.ACCESS)
      const downloadsGranted = (user.permissions || []).includes(PERMISSIONS.DOWNLOADS.ACCESS)
      const mergedPerms = downloadsGranted
        ? [...new Set([...rolePerms, PERMISSIONS.DOWNLOADS.ACCESS])]
        : rolePerms

      const userData = {
        ...user,
        permissions: mergedPerms,
        updated_by: userPermissions?.emailId || 'admin',
        updated_at: new Date().toISOString()
      }

      const success = await createOrUpdateUser(customer, user.emailId, userData)
      if (success) {
        await loadUsers()
        setEditingUser(null)
        setError('')
      } else {
        setError('Failed to update user')
      }
    } catch (err) {
      setError('Error updating user: ' + err.message)
    }
  }

  const handleDeleteUser = async (emailId) => {
    if (!window.confirm(`Are you sure you want to remove user ${emailId}?`)) {
      return
    }

    try {
      const success = await deleteUser(customer, emailId)
      if (success) {
        await loadUsers()
        setError('')
      } else {
        setError('Failed to delete user')
      }
    } catch (err) {
      setError('Error deleting user: ' + err.message)
    }
  }

  const handleStatusChange = async (emailId, status) => {
    try {
      const success = await updateUserStatus(customer, emailId, status)
      if (success) {
        await loadUsers()
        setError('')
      } else {
        setError('Failed to update user status')
      }
    } catch (err) {
      setError('Error updating status: ' + err.message)
    }
  }

  const getRoleDisplayName = (role) => {
    const roleNames = {
      [ROLES.SUPER_ADMIN]: 'Super Admin',
      [ROLES.CUSTOMER_ADMIN]: 'Customer Admin',
      [ROLES.DEVICE_ADMIN]: 'Device Admin',
      [ROLES.USER_ADMIN]: 'User Admin',
      [ROLES.REPORTS_ADMIN]: 'Reports Admin',
      [ROLES.DEVICE_VIEWER]: 'Device Viewer',
      [ROLES.REPORTS_VIEWER]: 'Reports Viewer'
    }
    return roleNames[role] || role
  }

  const getPermissionDisplayName = (key, permission) => {
    const permissionNames = {
      // Device permissions
      'VIEW': 'View Devices',
      'EDIT': 'Edit Device Settings',
      'ADD': 'Add New Devices',
      'DELETE': 'Delete Devices',
      'MAINTENANCE': 'Maintenance Mode',
      'LOGS': 'View Device Logs',
      'SERVICE_PIN': 'Configure Service PIN',
      'PAYMENT_CONFIG': 'Payment Configuration',
      'LAYOUT_EDIT': 'Edit Device Layout',
      'SLOT_CONFIG': 'Configure Slots',
      'CLEAR_LOCKOUTS': 'Clear Slot Lockouts',
      'RESTOCK': 'Restock Operations',
      // Reports permissions
      'ANALYTICS': 'Analytics Reports',
      'EXPORT': 'Export Reports',
      'CASH_TRANSACTIONS': 'Cash Transactions',
      'PAYMENT_GATEWAY': 'Payment Gateway Reports',
      'REFUNDS': 'Refund Reports'
    }
    return permissionNames[key] || key.replace('_', ' ').toLowerCase()
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'bg-success'
      case 'suspended': return 'bg-warning'
      case 'inactive': return 'bg-secondary'
      default: return 'bg-secondary'
    }
  }

  // If user doesn't have permission to view users
  if (!canViewUsers) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You don't have permission to view user management.
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-primary">
            <div className="card-header bg-white border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-dark">
                  <i className="fas fa-users me-2 text-primary"></i>
                  User Management
                  {users.length > 0 && (
                    <span className="badge bg-primary text-white ms-2" style={{ fontSize: '0.75rem' }}>
                      {users.length}
                    </span>
                  )}
                </h5>
                {canInviteUsers && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddUser(true)}
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Add User
                  </button>
                )}
              </div>
            </div>

            <div className="card-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Roles</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Created</th>
                        {canEditUsers && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.emailId}>
                          <td>
                            <div>
                              <strong>{user.metadata?.name || user.emailId}</strong>
                              <br />
                              <small className="text-muted">{user.emailId}</small>
                            </div>
                          </td>
                          <td>
                            {user.roles?.map((role) => (
                              <span key={role} className="badge bg-info me-1 mb-1">
                                {getRoleDisplayName(role)}
                              </span>
                            ))}
                            {(user.permissions || []).includes(PERMISSIONS.DOWNLOADS.ACCESS) && (
                              <span className="badge bg-secondary me-1 mb-1" title="Downloads Access">
                                DL
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(user.status)}`}>
                              {user.status || 'active'}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                            </small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </small>
                          </td>
                          {canEditUsers && (
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => setEditingUser(user)}
                                  title="Edit User"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                {user.status === 'active' ? (
                                  <button
                                    className="btn btn-outline-warning"
                                    onClick={() => handleStatusChange(user.emailId, 'suspended')}
                                    title="Suspend User"
                                  >
                                    <i className="fas fa-pause"></i>
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-outline-success"
                                    onClick={() => handleStatusChange(user.emailId, 'active')}
                                    title="Activate User"
                                  >
                                    <i className="fas fa-play"></i>
                                  </button>
                                )}
                                {canDeleteUsers && (
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteUser(user.emailId)}
                                    title="Delete User"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={canEditUsers ? 6 : 5} className="text-center text-muted py-4">
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New User</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddUser(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    value={newUser.emailId}
                    onChange={(e) => setNewUser({ ...newUser, emailId: e.target.value })}
                    placeholder="user@example.com"
                  />
                  <small className="text-muted">
                    Note: User must have a valid Firebase account and customer claim assigned by backend team
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newUser.metadata.name}
                    onChange={(e) => setNewUser({ 
                      ...newUser, 
                      metadata: { ...newUser.metadata, name: e.target.value }
                    })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Base Roles</label>
                  <select
                    multiple
                    className="form-select"
                    value={newUser.roles}
                    onChange={(e) => {
                      const selectedRoles = Array.from(e.target.selectedOptions, option => option.value)
                      setNewUser({ ...newUser, roles: selectedRoles })
                    }}
                  >
                    {Object.values(ROLES).filter(role => role !== ROLES.SUPER_ADMIN).map((role) => (
                      <option key={role} value={role}>
                        {getRoleDisplayName(role)}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Hold Ctrl/Cmd to select multiple roles</small>
                </div>
                
                {/* Device Access Control */}
                <div className="mb-3">
                  <label className="form-label">Device Access</label>
                  <div className="border rounded p-3">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newUser.device_restrictions.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUser({ ...newUser, device_restrictions: [] })
                          }
                        }}
                      />
                      <label className="form-check-label">
                        <strong>All Devices</strong> <small className="text-muted">(Unrestricted Access)</small>
                      </label>
                    </div>
                    <hr className="my-2" />
                    <div className="input-group input-group-sm mb-2">
                      <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search devices by name or location..."
                        value={deviceSearch}
                        onChange={(e) => setDeviceSearch(e.target.value)}
                      />
                      {deviceSearch && (
                        <button className="btn btn-outline-secondary" type="button" onClick={() => setDeviceSearch('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                      {(() => {
                        const q = deviceSearch.toLowerCase()
                        const filteredTouch = devices.filter(d =>
                          !q || (d.machine_name || '').toLowerCase().includes(q) || (d.id || '').toLowerCase().includes(q) || (d.location || '').toLowerCase().includes(q)
                        )
                        const filteredLegacy = legacyDevices.filter(d =>
                          !q || (d.name || '').toLowerCase().includes(q) || (d.location || '').toLowerCase().includes(q)
                        )
                        const noneMatch = filteredTouch.length === 0 && filteredLegacy.length === 0
                        return (
                          <>
                            {noneMatch && deviceSearch && (
                              <p className="text-muted text-center small py-2 mb-0">No devices match "{deviceSearch}"</p>
                            )}
                            {filteredTouch.length > 0 && (
                              <>
                                <div className="text-muted small fw-semibold mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                  <i className="fas fa-desktop me-1"></i>Devices
                                </div>
                                <div className="row mb-2">
                                  {filteredTouch.map((device) => (
                                    <div key={device.id} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={newUser.device_restrictions.includes(device.id)}
                                          onChange={(e) => {
                                            const deviceIds = [...newUser.device_restrictions]
                                            if (e.target.checked) {
                                              deviceIds.push(device.id)
                                            } else {
                                              const index = deviceIds.indexOf(device.id)
                                              if (index > -1) deviceIds.splice(index, 1)
                                            }
                                            setNewUser({ ...newUser, device_restrictions: deviceIds })
                                          }}
                                        />
                                        <label className="form-check-label">
                                          {device.machine_name || device.id}
                                          <small className="text-muted d-block">{device.location || 'No location'}</small>
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            {filteredLegacy.length > 0 && (
                              <>
                                {filteredTouch.length > 0 && <hr className="my-2" />}
                                <div className="text-muted small fw-semibold mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                  <i className="fas fa-server me-1"></i>Legacy Devices
                                </div>
                                <div className="row">
                                  {filteredLegacy.map((device) => (
                                    <div key={device._docId} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={newUser.device_restrictions.includes(device.name)}
                                          onChange={(e) => {
                                            const deviceIds = [...newUser.device_restrictions]
                                            if (e.target.checked) {
                                              deviceIds.push(device.name)
                                            } else {
                                              const index = deviceIds.indexOf(device.name)
                                              if (index > -1) deviceIds.splice(index, 1)
                                            }
                                            setNewUser({ ...newUser, device_restrictions: deviceIds })
                                          }}
                                        />
                                        <label className="form-check-label">
                                          {device.name}
                                          <small className="text-muted d-block">{device.location || 'No location'}</small>
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                  <small className="text-muted">
                    {newUser.device_restrictions.length === 0 
                      ? 'User has access to all devices' 
                      : `User restricted to ${newUser.device_restrictions.length} selected device(s)`}
                  </small>
                </div>

                {/* Downloads Access */}
                <div className="mb-3">
                  <label className="form-label">Downloads Access</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="newUserDownloads"
                      checked={newUser.custom_permissions.includes(PERMISSIONS.DOWNLOADS.ACCESS)}
                      onChange={(e) => {
                        const perms = [...newUser.custom_permissions]
                        if (e.target.checked) {
                          if (!perms.includes(PERMISSIONS.DOWNLOADS.ACCESS)) perms.push(PERMISSIONS.DOWNLOADS.ACCESS)
                        } else {
                          const idx = perms.indexOf(PERMISSIONS.DOWNLOADS.ACCESS)
                          if (idx > -1) perms.splice(idx, 1)
                        }
                        setNewUser({ ...newUser, custom_permissions: perms })
                      }}
                    />
                    <label className="form-check-label" htmlFor="newUserDownloads">
                      Allow access to the Downloads page (monthly CSV reports)
                    </label>
                  </div>
                </div>

                {/* Granular Permissions */}
                <div className="mb-3">
                  <label className="form-label">Granular Permissions</label>
                  <small className="text-muted d-block mb-2">Override role-based permissions with custom access controls</small>
                  
                  <div className="accordion" id="permissionsAccordion">
                    {/* Device Permissions */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#devicePermissions">
                          <i className="fas fa-desktop me-2"></i>Device Management
                        </button>
                      </h2>
                      <div id="devicePermissions" className="accordion-collapse collapse" data-bs-parent="#permissionsAccordion">
                        <div className="accordion-body">
                          <div className="row">
                            {Object.entries(PERMISSIONS.DEVICES).map(([key, permission]) => (
                              <div key={permission} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={newUser.custom_permissions.includes(permission)}
                                    onChange={(e) => {
                                      const perms = [...newUser.custom_permissions]
                                      if (e.target.checked) {
                                        perms.push(permission)
                                      } else {
                                        const index = perms.indexOf(permission)
                                        if (index > -1) perms.splice(index, 1)
                                      }
                                      setNewUser({ ...newUser, custom_permissions: perms })
                                    }}
                                  />
                                  <label className="form-check-label">
                                    {getPermissionDisplayName(key, permission)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Reports Permissions */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#reportsPermissions">
                          <i className="fas fa-chart-bar me-2"></i>Reports & Analytics
                        </button>
                      </h2>
                      <div id="reportsPermissions" className="accordion-collapse collapse" data-bs-parent="#permissionsAccordion">
                        <div className="accordion-body">
                          <div className="row">
                            {Object.entries(PERMISSIONS.REPORTS).map(([key, permission]) => (
                              <div key={permission} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={newUser.custom_permissions.includes(permission)}
                                    onChange={(e) => {
                                      const perms = [...newUser.custom_permissions]
                                      if (e.target.checked) {
                                        perms.push(permission)
                                      } else {
                                        const index = perms.indexOf(permission)
                                        if (index > -1) perms.splice(index, 1)
                                      }
                                      setNewUser({ ...newUser, custom_permissions: perms })
                                    }}
                                  />
                                  <label className="form-check-label">
                                    {getPermissionDisplayName(key, permission)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddUser(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddUser}
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingUser(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editingUser.emailId}
                    disabled
                  />
                  <small className="text-muted">Email cannot be changed</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingUser.metadata?.name || ''}
                    onChange={(e) => setEditingUser({ 
                      ...editingUser, 
                      metadata: { ...editingUser.metadata, name: e.target.value }
                    })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Base Roles</label>
                  <select
                    multiple
                    className="form-select"
                    value={editingUser.roles || []}
                    onChange={(e) => {
                      const selectedRoles = Array.from(e.target.selectedOptions, option => option.value)
                      setEditingUser({ ...editingUser, roles: selectedRoles })
                    }}
                  >
                    {Object.values(ROLES).filter(role => role !== ROLES.SUPER_ADMIN).map((role) => (
                      <option key={role} value={role}>
                        {getRoleDisplayName(role)}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Hold Ctrl/Cmd to select multiple roles</small>
                </div>

                {/* Device Access Control */}
                <div className="mb-3">
                  <label className="form-label">Device Access</label>
                  <div className="border rounded p-3">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={(editingUser.device_restrictions || []).length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingUser({ ...editingUser, device_restrictions: [] })
                          }
                        }}
                      />
                      <label className="form-check-label">
                        <strong>All Devices</strong> <small className="text-muted">(Unrestricted Access)</small>
                      </label>
                    </div>
                    <hr className="my-2" />
                    <div className="input-group input-group-sm mb-2">
                      <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search devices by name or location..."
                        value={deviceSearch}
                        onChange={(e) => setDeviceSearch(e.target.value)}
                      />
                      {deviceSearch && (
                        <button className="btn btn-outline-secondary" type="button" onClick={() => setDeviceSearch('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                      {(() => {
                        const q = deviceSearch.toLowerCase()
                        const filteredTouch = devices.filter(d =>
                          !q || (d.machine_name || '').toLowerCase().includes(q) || (d.id || '').toLowerCase().includes(q) || (d.location || '').toLowerCase().includes(q)
                        )
                        const filteredLegacy = legacyDevices.filter(d =>
                          !q || (d.name || '').toLowerCase().includes(q) || (d.location || '').toLowerCase().includes(q)
                        )
                        const noneMatch = filteredTouch.length === 0 && filteredLegacy.length === 0
                        return (
                          <>
                            {noneMatch && deviceSearch && (
                              <p className="text-muted text-center small py-2 mb-0">No devices match "{deviceSearch}"</p>
                            )}
                            {filteredTouch.length > 0 && (
                              <>
                                <div className="text-muted small fw-semibold mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                  <i className="fas fa-desktop me-1"></i>Devices
                                </div>
                                <div className="row mb-2">
                                  {filteredTouch.map((device) => (
                                    <div key={device.id} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={(editingUser.device_restrictions || []).includes(device.id)}
                                          onChange={(e) => {
                                            const deviceIds = [...(editingUser.device_restrictions || [])]
                                            if (e.target.checked) {
                                              deviceIds.push(device.id)
                                            } else {
                                              const index = deviceIds.indexOf(device.id)
                                              if (index > -1) deviceIds.splice(index, 1)
                                            }
                                            setEditingUser({ ...editingUser, device_restrictions: deviceIds })
                                          }}
                                        />
                                        <label className="form-check-label">
                                          {device.machine_name || device.id}
                                          <small className="text-muted d-block">{device.location || 'No location'}</small>
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            {filteredLegacy.length > 0 && (
                              <>
                                {filteredTouch.length > 0 && <hr className="my-2" />}
                                <div className="text-muted small fw-semibold mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                  <i className="fas fa-server me-1"></i>Legacy Devices
                                </div>
                                <div className="row">
                                  {filteredLegacy.map((device) => (
                                    <div key={device._docId} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={(editingUser.device_restrictions || []).includes(device.name)}
                                          onChange={(e) => {
                                            const deviceIds = [...(editingUser.device_restrictions || [])]
                                            if (e.target.checked) {
                                              deviceIds.push(device.name)
                                            } else {
                                              const index = deviceIds.indexOf(device.name)
                                              if (index > -1) deviceIds.splice(index, 1)
                                            }
                                            setEditingUser({ ...editingUser, device_restrictions: deviceIds })
                                          }}
                                        />
                                        <label className="form-check-label">
                                          {device.name}
                                          <small className="text-muted d-block">{device.location || 'No location'}</small>
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                  <small className="text-muted">
                    {(editingUser.device_restrictions || []).length === 0 
                      ? 'User has access to all devices' 
                      : `User restricted to ${(editingUser.device_restrictions || []).length} selected device(s)`}
                  </small>
                </div>

                {/* Downloads Access */}
                <div className="mb-3">
                  <label className="form-label">Downloads Access</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="editUserDownloads"
                      checked={(editingUser.permissions || []).includes(PERMISSIONS.DOWNLOADS.ACCESS)}
                      onChange={(e) => {
                        const perms = [...(editingUser.permissions || [])]
                        if (e.target.checked) {
                          if (!perms.includes(PERMISSIONS.DOWNLOADS.ACCESS)) perms.push(PERMISSIONS.DOWNLOADS.ACCESS)
                        } else {
                          const idx = perms.indexOf(PERMISSIONS.DOWNLOADS.ACCESS)
                          if (idx > -1) perms.splice(idx, 1)
                        }
                        setEditingUser({ ...editingUser, permissions: perms })
                      }}
                    />
                    <label className="form-check-label" htmlFor="editUserDownloads">
                      Allow access to the Downloads page (monthly CSV reports)
                    </label>
                  </div>
                </div>

                {/* Alert Threshold Settings */}
                <div className="mb-3">
                  <label className="form-label">Alert Threshold Settings</label>
                  <small className="text-muted d-block mb-2">Configure personalized alert thresholds and notification preferences</small>
                  
                  <div className="border rounded p-3">
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Critical Alert (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          value={editingUser.alert_thresholds?.stock?.critical || 20}
                          onChange={(e) => setEditingUser({
                            ...editingUser,
                            alert_thresholds: {
                              ...editingUser.alert_thresholds,
                              stock: {
                                ...editingUser.alert_thresholds?.stock,
                                critical: parseInt(e.target.value) || 0
                              }
                            }
                          })}
                        />
                        <small className="text-muted">Below this % triggers critical alerts</small>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Warning Alert (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          value={editingUser.alert_thresholds?.stock?.warning || 50}
                          onChange={(e) => setEditingUser({
                            ...editingUser,
                            alert_thresholds: {
                              ...editingUser.alert_thresholds,
                              stock: {
                                ...editingUser.alert_thresholds?.stock,
                                warning: parseInt(e.target.value) || 0
                              }
                            }
                          })}
                        />
                        <small className="text-muted">Below this % triggers warning alerts</small>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Low Stock Alert (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          value={editingUser.alert_thresholds?.stock?.low || 70}
                          onChange={(e) => setEditingUser({
                            ...editingUser,
                            alert_thresholds: {
                              ...editingUser.alert_thresholds,
                              stock: {
                                ...editingUser.alert_thresholds?.stock,
                                low: parseInt(e.target.value) || 0
                              }
                            }
                          })}
                        />
                        <small className="text-muted">Below this % triggers low stock alerts</small>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingUser.alert_thresholds?.enabled?.stockAlerts !== false}
                            onChange={(e) => setEditingUser({
                              ...editingUser,
                              alert_thresholds: {
                                ...editingUser.alert_thresholds,
                                enabled: {
                                  ...editingUser.alert_thresholds?.enabled,
                                  stockAlerts: e.target.checked
                                }
                              }
                            })}
                          />
                          <label className="form-check-label">
                            <i className="fas fa-boxes text-warning me-1"></i>
                            Stock Level Alerts
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingUser.alert_thresholds?.enabled?.deviceOffline !== false}
                            onChange={(e) => setEditingUser({
                              ...editingUser,
                              alert_thresholds: {
                                ...editingUser.alert_thresholds,
                                enabled: {
                                  ...editingUser.alert_thresholds?.enabled,
                                  deviceOffline: e.target.checked
                                }
                              }
                            })}
                          />
                          <label className="form-check-label">
                            <i className="fas fa-wifi text-danger me-1"></i>
                            Device Offline Alerts
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingUser.alert_thresholds?.enabled?.maintenance !== false}
                            onChange={(e) => setEditingUser({
                              ...editingUser,
                              alert_thresholds: {
                                ...editingUser.alert_thresholds,
                                enabled: {
                                  ...editingUser.alert_thresholds?.enabled,
                                  maintenance: e.target.checked
                                }
                              }
                            })}
                          />
                          <label className="form-check-label">
                            <i className="fas fa-tools text-info me-1"></i>
                            Maintenance Mode Alerts
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Granular Permissions */}
                <div className="mb-3">
                  <label className="form-label">Granular Permissions</label>
                  <small className="text-muted d-block mb-2">Override role-based permissions with custom access controls</small>
                  
                  <div className="accordion" id="editPermissionsAccordion">
                    {/* Device Permissions */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#editDevicePermissions">
                          <i className="fas fa-desktop me-2"></i>Device Management
                        </button>
                      </h2>
                      <div id="editDevicePermissions" className="accordion-collapse collapse" data-bs-parent="#editPermissionsAccordion">
                        <div className="accordion-body">
                          <div className="row">
                            {Object.entries(PERMISSIONS.DEVICES).map(([key, permission]) => (
                              <div key={permission} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={(editingUser.custom_permissions || []).includes(permission)}
                                    onChange={(e) => {
                                      const perms = [...(editingUser.custom_permissions || [])]
                                      if (e.target.checked) {
                                        perms.push(permission)
                                      } else {
                                        const index = perms.indexOf(permission)
                                        if (index > -1) perms.splice(index, 1)
                                      }
                                      setEditingUser({ ...editingUser, custom_permissions: perms })
                                    }}
                                  />
                                  <label className="form-check-label">
                                    {getPermissionDisplayName(key, permission)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Reports Permissions */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#editReportsPermissions">
                          <i className="fas fa-chart-bar me-2"></i>Reports & Analytics
                        </button>
                      </h2>
                      <div id="editReportsPermissions" className="accordion-collapse collapse" data-bs-parent="#editPermissionsAccordion">
                        <div className="accordion-body">
                          <div className="row">
                            {Object.entries(PERMISSIONS.REPORTS).map(([key, permission]) => (
                              <div key={permission} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={(editingUser.custom_permissions || []).includes(permission)}
                                    onChange={(e) => {
                                      const perms = [...(editingUser.custom_permissions || [])]
                                      if (e.target.checked) {
                                        perms.push(permission)
                                      } else {
                                        const index = perms.indexOf(permission)
                                        if (index > -1) perms.splice(index, 1)
                                      }
                                      setEditingUser({ ...editingUser, custom_permissions: perms })
                                    }}
                                  />
                                  <label className="form-check-label">
                                    {getPermissionDisplayName(key, permission)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={editingUser.status || 'active'}
                    onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleUpdateUser(editingUser)}
                >
                  Update User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement